/* eslint-disable local-rules/no-forbidden-words-code-warning */
import type { AfterViewInit, OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointsService } from 'breakpoints-service';

import type { SpineEventData } from '@app/shared/components';
import { SpineWebglAnimationComponent } from '@app/shared/components';
import { WINDOW } from '@app/shared/utils/window';

import { EventButtonComponent } from '../event-button/event-button.component';

/** Типы скинов свечения (редкость) */
export type GlowSkin =
  | '00_Standart'
  | '01_Consumer'
  | '02_Industrial'
  | '03_Mil-spec'
  | '04_Restricted'
  | '05_Classified'
  | '06_Covert'
  | '07_Extraordinary'
  | '08_Contraband'
  | '09_Cover';

/** Состояния анимации zip-pack */
export enum ZipPackState {
  IDLE = 'idle',
  HOLDING = 'holding',
  OPENING = 'opening',
  OPENED = 'opened',
}

/** Названия анимаций */
const ANIMATIONS = {
  WAIT: '01_wait',
  AUTO_CLICK: '02_auto_click',
  CLICK_HOLD: '02_click_hold',
  OPEN: '03_open',
} as const;

/** Названия хитбоксов */
const BOUNDING_BOXES = {
  CONTROLLER: 'BB_Ctrl_zip',
  BAG_LIMIT: 'BB_BAG',
  ZIP: 'BB_zip',
  OPEN_TRIGGER: 'BB_open',
} as const;

/** Название кости контроллера */
const CONTROLLER_BONE = 'Ctrl_zip';
const DEFAULT_PACK_SKIN_PREFIXES = ['packets', ''] as const;

@Component({
  selector: 'app-zip-pack-animation',
  standalone: true,
  templateUrl: './zip-pack-animation.component.html',
  styleUrl: './zip-pack-animation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SpineWebglAnimationComponent, EventButtonComponent],
})
export class ZipPackAnimationComponent implements AfterViewInit, OnDestroy {
  readonly #window = inject(WINDOW);
  readonly #breakpointsService = inject(BreakpointsService);

  /** Скин пакетика */
  readonly packSkin = input<string>('zip_1');

  /** Скин свечения (редкость карты) */
  readonly glowSkin = input<GlowSkin>('00_Standart');

  /** Путь к анимации */
  readonly animationPath = input<string>('/assets/spine-animations/ice-breakers/');
  readonly useBinarySkeleton = input<boolean>(true);
  readonly atlasFile = input<string>('zip.atlas.txt');
  readonly skelFile = input<string>('zip.skel.bytes');
  readonly jsonFile = input<string>('zip.json');
  readonly packSkinPrefixes = input<readonly string[]>(DEFAULT_PACK_SKIN_PREFIXES);

  /** Масштаб */
  readonly scale = input<number>(1);

  /** Событие спауна карты (callback из Spine) */
  readonly cardSpawned = output<void>();

  /** Событие завершения открытия */
  readonly packOpened = output<void>();

  readonly buttonText = input<string>('');

  readonly showButton = input<boolean>(true);

  readonly buttonLoading = input<boolean>(false);

  readonly buttonClick = output<void>();
  readonly animationLoaded = output<void>();

  readonly buttonTopPercent = signal<number>(80);

  /** Событие начала ручного открытия */
  readonly holdStarted = output<void>();

  /** Событие отпускания при ручном открытии */
  readonly holdReleased = output<void>();

  /** Событие прогресса разрыва (0-100) */
  readonly tearProgress = output<number>();

  /** Событие завершения разрыва */
  readonly tearFinished = output<void>();

  /** Ссылка на базовый Spine компонент */
  spineComponent = viewChild<SpineWebglAnimationComponent>('spineAnimation');

  readonly state = signal<ZipPackState>(ZipPackState.IDLE);
  readonly isAnimationLoaded = signal<boolean>(false);

  readonly isMobile = toSignal(this.#breakpointsService.isBreakpointActive$(['xs', 'ph']));

  /** Кэш найденных скинов */
  #cachedPackSkinName: string | null = null;
  #cachedGlowSkinName: string | null = null;

  /** Флаг удержания мыши */
  #isHolding = false;

  /** ID интервала проверки коллизий */
  #collisionCheckInterval: number | null = null;

  /** Начальная позиция контроллера */
  #originalBoneX = 0;
  #originalBoneY = 0;

  /** Начальная позиция для расчета прогресса разрыва */
  #tearStartX = 0;
  #tearStartY = 0;

  /** Целевая позиция (центр BB_open) для расчета прогресса */
  #tearTargetX = 0;
  #tearTargetY = 0;

  /** Расстояние от старта до цели */
  #tearTotalDistance = 0;

  /** Bound методы для событий */
  readonly #boundMouseDown: (e: MouseEvent) => void;
  readonly #boundMouseUp: (e: MouseEvent) => void;
  readonly #boundMouseMove: (e: MouseEvent) => void;
  readonly #boundTouchStart: (e: TouchEvent) => void;
  readonly #boundTouchEnd: (e: TouchEvent) => void;
  readonly #boundTouchMove: (e: TouchEvent) => void;

  constructor() {
    this.#boundMouseDown = this.#onMouseDown.bind(this);
    this.#boundMouseUp = this.#onMouseUp.bind(this);
    this.#boundMouseMove = this.#onMouseMove.bind(this);
    this.#boundTouchStart = this.#onTouchStart.bind(this);
    this.#boundTouchEnd = this.#onTouchEnd.bind(this);
    this.#boundTouchMove = this.#onTouchMove.bind(this);

    effect(() => {
      const packSkin = this.packSkin();
      const glowSkin = this.glowSkin();

      untracked(() => {
        if (this.isAnimationLoaded()) {
          this.#updateSkins(packSkin, glowSkin);
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.#addEventListeners();
  }

  ngOnDestroy(): void {
    this.#removeEventListeners();
    this.#stopCollisionCheck();
  }

  /** Обработчик загрузки анимации */
  onAnimationLoaded(): void {
    const spine = this.spineComponent();
    if (!spine?.skeleton) {
      return;
    }

    this.isAnimationLoaded.set(true);
    this.#updateSkins(this.packSkin(), this.glowSkin());
    this.playWaitAnimation();

    this.#calculateButtonPosition();
    this.animationLoaded.emit();
  }

  #updateSkins(packSkinName: string, glowSkinName: string): void {
    const spine = this.spineComponent();
    if (!spine?.skeleton) {
      return;
    }

    const possiblePackSkins = this.packSkinPrefixes().map((prefix) => (prefix ? `${prefix}/${packSkinName}` : packSkinName));
    const possibleGlowSkins = [`light_colors/${glowSkinName}`, glowSkinName];

    const foundPackSkin = possiblePackSkins.find((name) => spine.skeleton?.data.findSkin(name));
    const foundGlowSkin = possibleGlowSkins.find((name) => spine.skeleton?.data.findSkin(name));

    if (foundPackSkin === this.#cachedPackSkinName && foundGlowSkin === this.#cachedGlowSkinName) {
      return;
    }

    this.#cachedPackSkinName = foundPackSkin ?? null;
    this.#cachedGlowSkinName = foundGlowSkin ?? null;

    const skinsToApply: string[] = [];
    if (foundPackSkin) {
      skinsToApply.push(foundPackSkin);
    }
    if (foundGlowSkin) {
      skinsToApply.push(foundGlowSkin);
    }

    if (skinsToApply.length > 0) {
      spine.setCombinedSkin(skinsToApply);
    } else {
      const defaultSkin = spine.skeleton?.data.findSkin('default');
      if (defaultSkin) {
        spine.setSkin('default');
      }
    }
  }

  #calculateButtonPosition(): void {
    const spine = this.spineComponent();
    // const isMobile = this.isMobile();

    if (!spine?.bounds) {
      return;
    }

    const bagBounds = spine.getBoundingBox(BOUNDING_BOXES.BAG_LIMIT);
    if (!bagBounds) {
      return;
    }

    const bagBottomY = bagBounds.y;
    const canvasTopY = spine.bounds.offset.y + spine.bounds.size.y;

    const topPercent = ((canvasTopY - bagBottomY) / spine.bounds.size.y) * 100;

    // const offsetPercent = isMobile ? 45 : 32;
    const offsetPercent = 32;
    const finalPercent = topPercent + offsetPercent;

    this.buttonTopPercent.set(Math.max(0, Math.min(100, finalPercent)));
  }

  onSpineEvent(event: SpineEventData): void {
    if (event.name === 'spawn_card') {
      this.cardSpawned.emit();
    }
  }

  /** Обработчик завершения анимации */
  onAnimationComplete(animationName: string): void {
    if (animationName === ANIMATIONS.OPEN || animationName === ANIMATIONS.AUTO_CLICK) {
      this.state.set(ZipPackState.OPENED);
      this.packOpened.emit();
    }
  }

  /** Запустить idle анимацию (цикл) */
  playWaitAnimation(): void {
    const spine = this.spineComponent();
    if (!spine) {
      return;
    }

    this.state.set(ZipPackState.IDLE);
    spine.playAnimation(ANIMATIONS.WAIT, true, 0);
  }

  /** Запустить авто-открытие (по кнопке "Открыть") */
  playAutoOpenAnimation(): void {
    const spine = this.spineComponent();
    const currentState = this.state();

    if (!spine || currentState !== ZipPackState.IDLE) {
      return;
    }

    this.state.set(ZipPackState.OPENING);
    spine.playAnimation(ANIMATIONS.AUTO_CLICK, false, 0);
  }

  /** Применить комбинированные скины */
  #applyCombinedSkins(): void {
    const spine = this.spineComponent();
    if (!spine) {
      return;
    }

    const skinsToApply: string[] = [];
    if (this.#cachedPackSkinName) {
      skinsToApply.push(this.#cachedPackSkinName);
    }
    if (this.#cachedGlowSkinName) {
      skinsToApply.push(this.#cachedGlowSkinName);
    }

    if (skinsToApply.length > 0) {
      spine.setCombinedSkin(skinsToApply);
    }
  }

  #addEventListeners(): void {
    const canvas = this.spineComponent()?.spineCanvas()?.nativeElement;
    if (!canvas) {
      return;
    }

    canvas.addEventListener('mousedown', this.#boundMouseDown);
    this.#window?.addEventListener('mouseup', this.#boundMouseUp);
    this.#window?.addEventListener('mousemove', this.#boundMouseMove);
    canvas.addEventListener('touchstart', this.#boundTouchStart, { passive: false });
    this.#window?.addEventListener('touchend', this.#boundTouchEnd);
    this.#window?.addEventListener('touchmove', this.#boundTouchMove, { passive: false });
  }

  #removeEventListeners(): void {
    const canvas = this.spineComponent()?.spineCanvas()?.nativeElement;
    if (canvas) {
      canvas.removeEventListener('mousedown', this.#boundMouseDown);
      canvas.removeEventListener('touchstart', this.#boundTouchStart);
    }
    this.#window?.removeEventListener('mouseup', this.#boundMouseUp);
    this.#window?.removeEventListener('mousemove', this.#boundMouseMove);
    this.#window?.removeEventListener('touchend', this.#boundTouchEnd);
    this.#window?.removeEventListener('touchmove', this.#boundTouchMove);
  }

  #onMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.#startHold(event.clientX, event.clientY);
  }

  #onMouseUp(): void {
    this.#endHold();
  }

  #onMouseMove(event: MouseEvent): void {
    if (!this.#isHolding) {
      return;
    }
    this.#updateControllerPosition(event.clientX, event.clientY);
  }

  #onTouchStart(event: TouchEvent): void {
    event.preventDefault();
    const touch = event.touches[0];
    if (touch) {
      this.#startHold(touch.clientX, touch.clientY);
    }
  }

  #onTouchEnd(): void {
    this.#endHold();
  }

  #onTouchMove(event: TouchEvent): void {
    if (!this.#isHolding) {
      return;
    }
    event.preventDefault();
    const touch = event.touches[0];
    if (touch) {
      this.#updateControllerPosition(touch.clientX, touch.clientY);
    }
  }

  #startHold(clientX: number, clientY: number): void {
    if (this.state() === ZipPackState.OPENED || this.state() === ZipPackState.OPENING) {
      return;
    }

    const spine = this.spineComponent();
    if (!spine?.skeleton || !spine.bounds) {
      return;
    }

    // Проверяем, что клик попал на BB_zip
    if (!this.#isClickOnZipBounds(spine, clientX, clientY)) {
      return;
    }

    // Сохраняем начальную позицию контроллера
    const controllerBone = spine.skeleton.findBone(CONTROLLER_BONE);
    if (controllerBone) {
      this.#originalBoneX = this.#getBoneX(controllerBone);
      this.#originalBoneY = this.#getBoneY(controllerBone);
    }

    // Вычисляем начальную позицию и цель для расчета прогресса разрыва
    this.#initializeTearProgress(spine, clientX, clientY);

    this.#isHolding = true;
    this.state.set(ZipPackState.HOLDING);

    spine.playAnimation(ANIMATIONS.CLICK_HOLD, true, 0);

    this.holdStarted.emit();
    this.#startCollisionCheck();
  }

  #isClickOnZipBounds(spine: SpineWebglAnimationComponent, clientX: number, clientY: number): boolean {
    const canvas = spine.spineCanvas()?.nativeElement;
    if (!canvas || !spine.bounds) {
      return false;
    }

    const zipBounds = spine.getBoundingBox(BOUNDING_BOXES.ZIP);
    if (!zipBounds) {
      return false;
    }

    const rect = canvas.getBoundingClientRect();

    // Конвертируем координаты клика в Spine координаты
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    const spineX = spine.bounds.offset.x + (mouseX / rect.width) * spine.bounds.size.x;
    const spineY = spine.bounds.offset.y + ((rect.height - mouseY) / rect.height) * spine.bounds.size.y;

    // Проверяем попадание в BB_zip
    return (
      spineX >= zipBounds.x && spineX <= zipBounds.x + zipBounds.width && spineY >= zipBounds.y && spineY <= zipBounds.y + zipBounds.height
    );
  }

  #endHold(): void {
    if (!this.#isHolding) {
      return;
    }

    this.#isHolding = false;
    this.#stopCollisionCheck();

    // Возвращаем контроллер в исходную позицию
    const spine = this.spineComponent();
    if (spine?.skeleton) {
      const controllerBone = spine.skeleton.findBone(CONTROLLER_BONE);
      if (controllerBone) {
        this.#setBonePosition(controllerBone, this.#originalBoneX, this.#originalBoneY);
      }
    }

    // Если не открыли - возвращаемся к idle
    if (this.state() === ZipPackState.HOLDING) {
      this.playWaitAnimation();
      this.holdReleased.emit();
    }
  }

  #updateControllerPosition(clientX: number, clientY: number): void {
    const spine = this.spineComponent();
    if (!spine?.skeleton || !spine.bounds) {
      return;
    }

    const canvas = spine.spineCanvas()?.nativeElement;
    if (!canvas) {
      return;
    }

    const rect = canvas.getBoundingClientRect();

    // Конвертируем координаты мыши в координаты Spine
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    // Преобразуем в Spine координаты (Y инвертирован)
    let spineX = spine.bounds.offset.x + (mouseX / rect.width) * spine.bounds.size.x;
    let spineY = spine.bounds.offset.y + ((rect.height - mouseY) / rect.height) * spine.bounds.size.y;

    const bagBounds = spine.getBoundingBox(BOUNDING_BOXES.BAG_LIMIT);
    if (bagBounds) {
      const paddingX = bagBounds.width * 0.1;
      const paddingY = bagBounds.height * 0.1;

      const minX = bagBounds.x + paddingX;
      const maxX = bagBounds.x + bagBounds.width - paddingX;
      const minY = bagBounds.y + paddingY;
      const maxY = bagBounds.y + bagBounds.height - paddingY;

      spineX = Math.max(minX, Math.min(maxX, spineX));
      spineY = Math.max(minY, Math.min(maxY, spineY));
    }

    // Находим кость контроллера
    const controllerBone = spine.skeleton.findBone(CONTROLLER_BONE);
    if (!controllerBone) {
      return;
    }

    // Двигаем кость
    this.#setBonePosition(controllerBone, spineX, spineY);

    // Вычисляем и эмитим прогресс разрыва
    this.#calculateAndEmitTearProgress(spineX, spineY);
  }

  // TODO(Spine 4.3 cleanup): в Spine 4.3 координаты кости лежат в `bone.pose.x/y`, а старые runtime-формы использовали
  // прямые `bone.x/y`. Оставляем этот compatibility shim на время миграции. Когда все zip-pack animation paths будут
  // проверены на WebGL runtime 4.3, нужно типизировать controller bones как Spine `Bone` и заменить эти helpers на прямое
  // чтение и запись `bone.pose.x/y`.
  #getBoneX(bone: unknown): number {
    const pose = this.#getBonePose(bone);
    const x = pose?.x ?? (bone as { x?: unknown }).x;

    return typeof x === 'number' ? x : 0;
  }

  #getBoneY(bone: unknown): number {
    const pose = this.#getBonePose(bone);
    const y = pose?.y ?? (bone as { y?: unknown }).y;

    return typeof y === 'number' ? y : 0;
  }

  #setBonePosition(bone: unknown, x: number, y: number): void {
    const pose = this.#getBonePose(bone);

    if (pose) {
      pose.x = x;
      pose.y = y;
      return;
    }

    const legacyBone = bone as { x?: number; y?: number };
    legacyBone.x = x;
    legacyBone.y = y;
  }

  #getBonePose(bone: unknown): { x?: number; y?: number } | null {
    const pose = (bone as { pose?: unknown }).pose;

    return typeof pose === 'object' && pose !== null ? (pose as { x?: number; y?: number }) : null;
  }

  #startCollisionCheck(): void {
    this.#stopCollisionCheck();

    this.#collisionCheckInterval =
      this.#window?.setInterval(() => {
        this.#checkCollision();
      }, 16) ?? null; // ~60fps
  }

  #stopCollisionCheck(): void {
    if (this.#collisionCheckInterval !== null) {
      this.#window?.clearInterval(this.#collisionCheckInterval);
      this.#collisionCheckInterval = null;
    }
  }

  #checkCollision(): void {
    const spine = this.spineComponent();
    if (!spine || this.state() !== ZipPackState.HOLDING) {
      return;
    }

    // Проверяем столкновение BB_zip и BB_open
    const hasCollision = spine.checkBoundingBoxCollision(BOUNDING_BOXES.ZIP, BOUNDING_BOXES.OPEN_TRIGGER);

    if (hasCollision) {
      this.#triggerOpen();
    }
  }

  #triggerOpen(): void {
    this.#stopCollisionCheck();
    this.#isHolding = false;
    this.state.set(ZipPackState.OPENING);

    // Эмитим финальный прогресс и событие завершения разрыва
    this.tearProgress.emit(100);
    this.tearFinished.emit();

    const spine = this.spineComponent();
    if (spine) {
      spine.playAnimation(ANIMATIONS.OPEN, false, 0);
    }
  }

  #initializeTearProgress(spine: SpineWebglAnimationComponent, clientX: number, clientY: number): void {
    const canvas = spine.spineCanvas()?.nativeElement;
    if (!canvas || !spine.bounds) {
      return;
    }

    const rect = canvas.getBoundingClientRect();

    // Конвертируем начальную позицию клика в Spine координаты
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    this.#tearStartX = spine.bounds.offset.x + (mouseX / rect.width) * spine.bounds.size.x;
    this.#tearStartY = spine.bounds.offset.y + ((rect.height - mouseY) / rect.height) * spine.bounds.size.y;

    // Получаем позицию BB_open (центр) как цель
    const openBounds = spine.getBoundingBox(BOUNDING_BOXES.OPEN_TRIGGER);
    if (openBounds) {
      this.#tearTargetX = openBounds.x + openBounds.width / 2;
      this.#tearTargetY = openBounds.y + openBounds.height / 2;

      // Вычисляем общее расстояние от старта до цели
      const dx = this.#tearTargetX - this.#tearStartX;
      const dy = this.#tearTargetY - this.#tearStartY;
      this.#tearTotalDistance = Math.sqrt(dx * dx + dy * dy);
    }
  }

  #calculateAndEmitTearProgress(spineX: number, spineY: number): void {
    if (this.#tearTotalDistance === 0) {
      return;
    }

    const toTargetX = this.#tearTargetX - this.#tearStartX;
    const toTargetY = this.#tearTargetY - this.#tearStartY;
    const currentX = spineX - this.#tearStartX;
    const currentY = spineY - this.#tearStartY;

    const dotProduct = currentX * toTargetX + currentY * toTargetY;
    const projectionLength = dotProduct / this.#tearTotalDistance;

    const progress = Math.max(0, Math.min(100, (projectionLength / this.#tearTotalDistance) * 100));

    this.tearProgress.emit(progress);
  }
}
